<template>
  <b-container fluid>
    <esg-base-card title="탄소중립 비용"
                   :footer="false">

      <template #actions>
        <!-- Corporation search form -->
        <b-form-input placeholder="법인을 선택해주세요"
                      v-model="corporation.name"
                      v-b-modal.corporation-search
                      readonly/>
        <!-- /Corporation search form -->

        <!-- Business location search box -->
        <b-form-input placeholder="사업장을 선택해주세요"
                      v-model="businessLocation.name"
                      @click="openBusinessLocationSearch"
                      readonly/>
        <!-- /Business location search box -->

        <!-- Clear button -->
        <esg-base-button size="sm"
                         @click="clearEntities">Clear
        </esg-base-button>
        <!-- /Clear button -->
      </template>

      <!-- Corporation search modal -->
      <esg-base-dashboard-modal id="corporation-search"
                                size="lg"
                                title="법인 선택">
        <esg-base-list name="esg_base_corporation"
                       @rowClicked="setCorporation"/>
      </esg-base-dashboard-modal>
      <!-- /Corporation search modal -->

      <!-- Business location search modal -->
      <esg-base-dashboard-modal id="business-location-search"
                                size="lg"
                                title="사업장 선택">
        <esg-base-list name="business-location"
                       :fixed-filter="businessLocationFilter"
                       @rowClicked="setBusinessLocation"/>
      </esg-base-dashboard-modal>
      <!-- /Business location search modal -->

      <b-container fluid>
        <b-row align-h="start">
          <b-col md="6"
                 lg="3">
            <esg-single-score-card title="총비용"
                                   :score="formatNumber(total/ 10e8, 0)"
                                   score-unit="억원"
                                   color="yellow"
                                   diff-unit="%"
                                   :loading="loading"/>
          </b-col>
          <b-col md="6"
                 lg="3">
            <esg-single-score-card title="Scope 1 감축 비용"
                                   :score="formatNumber(scopeOneTotal/ 10e8, 0)"
                                   score-unit="억원"
                                   color="green"
                                   diff-unit="%"
                                   :loading="loading"/>
          </b-col>
          <b-col md="6"
                 lg="3">
            <esg-single-score-card title="Scope 2 감축 비용"
                                   :score="formatNumber(scopeTwoTotal/ 10e8, 0)"
                                   score-unit="억원"
                                   color="green"
                                   diff-unit="%"
                                   :loading="loading"/>
          </b-col>
        </b-row>

        <b-row align-h="center">
          <b-col>
            <esg-base-dashboard-card title="연도별 탄소중립 비용">

              <template #actions>
                <esg-base-select-box label="기간"
                                     v-model="lastYear">
                  <option value="2030">단기</option>
                  <option value="2050">장기</option>
                </esg-base-select-box>

                <esg-base-select-box label="유형"
                                     v-model="chartType">
                  <option value="">CAPEX / OPEX</option>
                  <option value="C">CAPEX 상세</option>
                  <option value="O">OPEX 상세</option>
                </esg-base-select-box>
              </template>

              <carbon-cost-by-year :type="chartType"
                                   :corporation="corporation"
                                   :business-location="businessLocation"
                                   :last-year="Number(lastYear)"/>

              <div class="content-title mt-3">감축수단별 비용 상세 (단위: 억원)</div>
              <carbon-cost-by-method v-if="!chartType"
                                     :corporation="corporation"
                                     :business-location="businessLocation"
                                     :last-year="Number(lastYear)"/>
              <carbon-cost-by-method-detail v-else
                                            :type="chartType"
                                            :corporation="corporation"
                                            :business-location="businessLocation"
                                            :last-year="Number(lastYear)"/>
            </esg-base-dashboard-card>
          </b-col>
        </b-row>

        <esg-base-dashboard-card class="mt-3"
                                 title="탄소중립 비용 (Scope 단위)">
          <b-row class="mt-2">
            <b-col cols="12"
                   lg="6">
              <div class="content-title">Scope 1</div>
              <carbon-cost-by-scope :scope="1"
                                    :corporation="corporation"
                                    :business-location="businessLocation"/>
            </b-col>
            <b-col cols="12"
                   lg="6">
              <div class="content-title">Scope 2</div>
              <carbon-cost-by-scope :scope="2"
                                    :corporation="corporation"
                                    :business-location="businessLocation"/>
            </b-col>
          </b-row>
          <div class="content-title mt-3">감축수단별 비용 상세</div>
          <carbon-cost-by-scope-detail :corporation="corporation"
                                       :business-location="businessLocation"/>
        </esg-base-dashboard-card>

        <esg-base-dashboard-card class="mt-3"
                                 title="톤당 감축비용 (감축수단 단위)">
          <abatement-cost-by-method :corporation="corporation"
                                    :business-location="businessLocation"/>
          <abatement-cost-by-method-detail :corporation="corporation"
                                           :business-location="businessLocation"/>
        </esg-base-dashboard-card>
      </b-container>
    </esg-base-card>
  </b-container>
</template>

<script>
import EsgBaseDashboardCard from "@/components/esg/base/EsgBaseDashboardCard.vue"
import EsgSingleScoreCard from "@/components/esg/base/EsgSingleScoreCard.vue";
import EsgBaseCard from "@/components/esg/base/EsgBaseCard.vue";
import EsgBaseSelectBox from "@/components/esg/base/form/EsgBaseSelectBox.vue";
import EsgBaseDashboardModal from "@/components/esg/base/EsgBaseDashboardModal.vue";
import EsgBaseList from "@/components/esg/base/EsgBaseList.vue";
import EsgBaseButton from "@/components/esg/base/EsgBaseButton.vue";

import CarbonCostByYear from "@/components/esg/3. Monitoring/charts2/CarbonCostByYear.vue";
import CarbonCostByMethod from "@/components/esg/3. Monitoring/tables/CarbonCostByMethod.vue";
import CarbonCostByMethodDetail from "@/components/esg/3. Monitoring/tables/CarbonCostByMethodDetail.vue";
import CarbonCostByScope from "@/components/esg/3. Monitoring/charts2/CabonCostByScope.vue"
import CarbonCostByScopeDetail from "@/components/esg/3. Monitoring/tables/CarbonCostByScopeDetail.vue";
import AbatementCostByMethodDetail from "@/components/esg/3. Monitoring/tables/AbatementCostByMethodDetail.vue";
import AbatementCostByMethod from "@/components/esg/3. Monitoring/charts2/AbatementCostByMethod.vue";

export default {
  name: 'EsgCarbonNeutralCost',
  components: {
    AbatementCostByMethod,
    AbatementCostByMethodDetail,
    CarbonCostByScopeDetail,
    EsgBaseButton,
    EsgBaseList, EsgBaseDashboardModal,
    EsgBaseSelectBox,
    EsgBaseCard,
    EsgBaseDashboardCard,
    EsgSingleScoreCard,

    CarbonCostByYear,
    CarbonCostByMethod,
    CarbonCostByMethodDetail,
    CarbonCostByScope,
  },
  data() {
    return {
      loading: false,
      chartType: null,
      total: 0,
      lastYear: 2050,
      scopeOneTotal: 0,
      scopeTwoTotal: 0,
      corporation: {},
      businessLocation: {},
      businessLocationFilter: []
    }
  },
  computed: {
    currentEntity() {
      return this.businessLocation.name || this.corporation.name || '전사'
    }
  },
  watch: {
    corporation: {
      deep: true,
      handler() {
        this.businessLocation = {};
        this.fetchTotals();
      }
    },
    businessLocation: {
      deep: true,
      handler() {
        this.fetchTotals();
      }
    }
  },
  created() {
    this.fetchTotals();
  },
  beforeDestroy() {
    this.abortAjax();
  },
  methods: {
    async fetchTotals() {
      try {
        this.loading = true;

        // Construct query arguments
        let args = { groupKeys: ['scope'] };
        this.corporation.name && (args.corporations = [this.corporation.code])
        this.businessLocation.name && (args.businessLocations = [this.businessLocation.business_location_code])

        let data = new FormData()
        data.append('path', 'esg/cost/main');
        data.append('json', JSON.stringify([args]));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }
        let list = JSON.parse(response.data.data)[0].data

        const scopeOneData = list.find(row => row.scope === 1);
        const scopeTwoData = list.find(row => row.scope === 2);

        this.scopeOneTotal = scopeOneData ? scopeOneData.cost_total : 0;
        this.scopeTwoTotal = scopeTwoData ? scopeTwoData.cost_total : 0;
        this.total = this.scopeOneTotal + this.scopeTwoTotal;
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async openBusinessLocationSearch() {
      if (this.corporation && this.corporation.name && this.corporation.code) {
        this.$bvModal.show('business-location-search')
      } else {
        await this.showError(new Error('법인을 먼저 선택해주세요.'));
      }
    },
    setCorporation(corporation) {
      this.corporation = corporation;
      this.businessLocationFilter = [{
        name: 'esg_base_business_location.corporation_code',
        operation: 'EQUAL',
        value: this.corporation.code
      }];
      this.$bvModal.hide('corporation-search')
    },
    setBusinessLocation(businessLocation) {
      this.businessLocation = {
        name: businessLocation.esg_base_business_location_name,
        corporation_code: businessLocation.esg_base_corporation_code,
        business_location_code: businessLocation.esg_base_business_location_business_location_code,
      }
      this.$bvModal.hide('business-location-search')
    },
    clearEntities() {
      this.corporation = {};
      this.businessLocation = {};
    }
  }
}
</script>

<style scoped>
.content-title {
  font-weight: 600;
  margin-left: 8px;
  margin-right: 8px;
}

.form-control {
  padding: 0.533rem 0.8rem 0.533rem 1.066rem;
  height: 2.666rem !important;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #F6F6FA;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150% !important;
  margin-bottom: 0;
}

.form-control[readonly] {
  background-color: #F6F6FA;
}

.form-control:focus {
  box-shadow: none;
  border-color: unset;
}
</style>
